<script setup lang="ts">
import { useId } from 'vue';

/**
 * A labelled control that keeps its label, hint and error tied together for
 * assistive technology.
 *
 * The id is generated rather than asked for: every one of these needs a unique
 * one to link `<label for>` and `aria-describedby`, and making each caller
 * invent one is how two fields end up sharing an id and the label stops
 * pointing at the right input.
 *
 * `multiline` swaps the `<input>` for a `<textarea>` and changes nothing else.
 * The wiring above is the part worth having in one place, and a second
 * component would be the same accessibility code written twice.
 */
// Every optional prop spells out `| undefined`: under
// `exactOptionalPropertyTypes` a bare `error?: string` refuses an explicitly
// passed `undefined`, which is precisely what a caller binding a lookup that
// found nothing hands over.
const props = withDefaults(
  defineProps<{
    label: string;
    type?: 'text' | 'email' | 'password' | undefined;
    autocomplete?: string | undefined;
    hint?: string | undefined;
    error?: string | undefined;
    required?: boolean | undefined;
    inputmode?: 'text' | 'numeric' | 'email' | undefined;
    maxlength?: number | undefined;
    placeholder?: string | undefined;
    autofocus?: boolean | undefined;
    multiline?: boolean | undefined;
    rows?: number | undefined;
  }>(),
  {
    type: 'text',
    required: true,
    autocomplete: undefined,
    hint: undefined,
    error: undefined,
    inputmode: undefined,
    maxlength: undefined,
    placeholder: undefined,
    autofocus: false,
    multiline: false,
    rows: 4,
  },
);

const model = defineModel<string>({ required: true });

const id = useId();
const hintId = `${id}-hint`;
const errorId = `${id}-error`;

// Both are referenced when both exist, so the error does not silently replace a
// hint the user still needs.
const describedBy = (): string | undefined => [props.hint ? hintId : '', props.error ? errorId : ''].filter(Boolean).join(' ') || undefined;
</script>

<template>
  <div class="hb-field">
    <label class="hb-field__label" :for="id">{{ label }}</label>
    <textarea
      v-if="multiline"
      :id="id"
      v-model="model"
      class="hb-field__input hb-field__input--multiline"
      :class="{ 'hb-field__input--invalid': Boolean(error) }"
      :rows="rows"
      :required="required"
      :maxlength="maxlength"
      :placeholder="placeholder"
      :autofocus="autofocus"
      :aria-invalid="error ? true : undefined"
      :aria-describedby="describedBy()"
    />
    <input
      v-else
      :id="id"
      v-model="model"
      class="hb-field__input"
      :class="{ 'hb-field__input--invalid': Boolean(error) }"
      :type="type"
      :autocomplete="autocomplete"
      :required="required"
      :inputmode="inputmode"
      :maxlength="maxlength"
      :placeholder="placeholder"
      :autofocus="autofocus"
      :aria-invalid="error ? true : undefined"
      :aria-describedby="describedBy()"
    />
    <p v-if="hint" :id="hintId" class="hb-field__hint">{{ hint }}</p>
    <p v-if="error" :id="errorId" class="hb-field__error">{{ error }}</p>
  </div>
</template>

<style scoped>
.hb-field {
  display: flex;
  flex-direction: column;
  gap: var(--hb-space-2);
}

.hb-field__label {
  font-size: var(--hb-text-sm);
  font-weight: 500;
}

.hb-field__input {
  /* Touch first, and 16px specifically: iOS Safari zooms the page in on focus
     for anything smaller, which then leaves the layout scrolled sideways. */
  min-height: var(--hb-touch-target);
  padding: 0 var(--hb-space-3);
  background: var(--hb-surface-sunken);
  border: 1px solid var(--hb-border);
  border-radius: var(--hb-radius-md);
  color: var(--hb-text);
  font-family: inherit;
  font-size: var(--hb-text-md);
}

.hb-field__input--multiline {
  padding: var(--hb-space-3);
  line-height: 1.5;
  resize: vertical;
}

.hb-field__input::placeholder {
  color: var(--hb-text-subtle);
}

.hb-field__input:focus-visible {
  border-color: var(--hb-accent);
}

.hb-field__input--invalid {
  border-color: var(--hb-danger);
}

.hb-field__hint {
  color: var(--hb-text-muted);
  font-size: var(--hb-text-xs);
}

.hb-field__error {
  color: var(--hb-danger);
  font-size: var(--hb-text-xs);
}
</style>
